<template>
  <div
    v-if="option"
    class="style-option"
  >
    <section class="style-option__left">
      <ImagePreview
        :image-url="getTrimmedImage(option.assets_file)"
        @image-change="onImageChange"
      />
    </section>
    <section class="style-option__right">
      <h3>{{ optionTitle }}</h3>
      <div class="style-option__select-group">
        <ElSelect
          v-for="(filterGroup, key) in optionMetaFilters"
          :key="key"
          v-model="meta[key]"
          class="style-option__select"
          :placeholder="filterGroup.name"
          size="mini"
          filterable
          clearable
          no-match-text="No match"
          @change="value => onMetaChange(key, value)"
        >
          <ElOption
            v-for="value in filterGroup.values"
            :key="value.id"
            :value="value.id"
            :label="value.name"
          />
        </ElSelect>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import { imageResolutions } from '@/constants/image.js'

import { S3Trim } from '@/mixins/s3Trim.js'
import { optionMetaKeys } from '@/constants/meta.js'
import Utils from '@/services/utils'

import ImagePreview from '@/components/ImagePreview.vue'

export default {
  name: 'StyleOption',

  components: {
    ImagePreview
  },

  mixins: [S3Trim],

  props: {
    styleId: {
      required: true,
      type: Number
    },
    optionId: {
      required: true,
      type: Number
    },

    optionTitle: String,
    optionMetaFilters: {
      type: Object,
      default: () => ({})
    }
  },

  data: () => ({
    meta: {},
    fileNew: null
  }),

  computed: {
    ...mapGetters(['getOptionById', 'getOptionMetaFilters']),

    /**
     * @returns {string}
     */
    imageResolution () {
      return imageResolutions.STYLE_THUMBNAIL
    },

    /**
     * @returns {string}
     */
    option () {
      return this.getOptionById(this.optionId)
    }
  },

  watch: {
    /**
     */
    option () {
      this.setInitialData()
    }
  },

  created () {
    this.setInitialData()
  },

  methods: {
    ...mapActions(['updateOption', 'setLatestDeliveryAssigned']),

    /**
     */
    setInitialData () {
      this.meta = Utils.parseOptionMeta(this.option)
    },

    /**
     * @param   {string} url
     *
     * @returns {string}
     */
    getTrimmedImage (url) {
      return this.s3Trim(url, this.imageResolution)
    },

    /**
     * @param {string} name
     * @param {number} value
     */
    async onMetaChange (name, value) {
      await this.saveOption({
        [name]: value
      })

      const keyDeliveryId = optionMetaKeys.DELIVERY_ID

      if (name === keyDeliveryId) {
        const delivery = this.optionMetaFilters[keyDeliveryId].values.find(
          delivery => delivery.id === value
        )

        this.setLatestDeliveryAssigned(delivery)
      }
    },

    /**
     * @param {Event} imageChangeEvent
     */
    onImageChange (imageChangeEvent) {
      this.fileNew = imageChangeEvent.image.file
      this.saveOption()
      this.fileNew = null
    },

    /**
     * @param {object} meta
     */
    saveOption (meta) {
      const payload = {
        optionId: this.optionId,
        styleId: this.styleId,
        meta,
        file: this.fileNew
      }

      this.updateOption(payload)
    }
  }
}
</script>

<style lang="scss" scoped>
$image-size: $spacer-unit-x8;
$option-border-radius: $border-radius-default / 2;

.style-option {
  display: flex;
  justify-content: left;
  width: 100%;
  padding: $spacer-unit-x2;
  text-align: left;
  border-radius: $option-border-radius;
}

.style-option__left {
  @include size($image-size);

  position: relative;
  margin-right: $spacer-unit-x2;
}

.style-option__right {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-around;
  margin-right: $spacer-unit-x2;

  .style-option__select {
    margin-right: $spacer-unit-x2;

    &:last-child {
      margin-right: 0;
    }
  }

  .style-option__select ~ .style-option__select {
    margin-bottom: 0;
  }
}

.style-option__select-group {
  display: flex;
  justify-content: flex-start;
}
</style>
